import { useLayoutEffect, useRef, useState } from "react"
import { App, Button, Flex, Typography } from "antd"
import { FileImageOutlined } from "@ant-design/icons"
import { uploadFile } from "~/api/upload"
import {
  generateVideoCover,
  getVideoCoverErrorMessage,
} from "~/common/media/videoCover"
import { useResourceUploadActivity } from "~/components/ResourceUploadField/UploadScope"
import { VideoDownloadPolicy, VideoPlayer } from "~/components/VideoPlayer"
import styles from "./index.css"

export const VideoCoverPreview = ({
  src,
  cover,
  disabled,
  onCover,
}: {
  src: string
  cover: string
  disabled: boolean
  onCover: (url: string) => void
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const operation = useRef<AbortController | null>(null)
  const [duration, setDuration] = useState(0)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState("")
  const { message } = App.useApp()
  useResourceUploadActivity(pending)
  useLayoutEffect(() => () => operation.current?.abort(), [])

  const generate = async () => {
    const video = videoRef.current
    if (!video || disabled || operation.current || !duration) return
    const controller = new AbortController()
    operation.current = controller
    setPending(true)
    setError("")
    try {
      const file = await generateVideoCover(
        video,
        video.currentTime,
        controller.signal,
      )
      const { data } = await uploadFile({
        file,
        filename: "video-cover.jpg",
        type: "image",
        signal: controller.signal,
      })
      if (controller.signal.aborted) return
      onCover(data.url)
      void message.success("已设为模板封面，保存模板后生效")
    } catch (failure) {
      if (!controller.signal.aborted)
        setError(getVideoCoverErrorMessage(failure))
    } finally {
      if (!controller.signal.aborted) {
        operation.current = null
        setPending(false)
      }
    }
  }

  return (
    <Flex
      vertical
      gap={8}
      data-testid="template-case-video"
    >
      <VideoDownloadPolicy value={true}>
        <VideoPlayer
          ref={videoRef}
          className={styles.caseVideo}
          crossOrigin="anonymous"
          src={src}
          poster={cover || undefined}
          controls
          muted
          objectFit="contain"
          onLoadedMetadata={(event) => {
            const value = event.currentTarget.duration
            setDuration(Number.isFinite(value) ? value : 0)
            setError("")
          }}
          onError={() => {
            setDuration(0)
            setError("视频加载失败，请检查链接及跨域访问设置")
          }}
        />
      </VideoDownloadPolicy>
      <Flex
        align="center"
        gap={8}
        wrap
      >
        <Button
          icon={<FileImageOutlined />}
          loading={pending}
          disabled={disabled || !duration}
          onClick={() => void generate()}
        >
          生成并设为封面
        </Button>
      </Flex>
      {error && (
        <Typography.Text
          type="danger"
          role="alert"
        >
          {error}
        </Typography.Text>
      )}
    </Flex>
  )
}
